// Client-side helpers for fetching done_summary_broker_breakdown / done_summary_haka_haki
// CSVs directly from Azure Blob Storage (trading-data-v2), using a SAS token issued by
// the backend gate endpoint (GET /api/orderbook-replay/sas-token). Mirrors the direct-blob
// pattern used by BrokerSummaryPage.tsx / brokerSummaryBlob.ts, ported from the
// /done-summary/:stockCode/:date route in backend/src/routes/brokerbreakdown.ts.

import { blobCacheKey } from './cacheKey';
import { fetchZipEntry } from './zipRangeReader';

const NUMERIC_FIELDS = new Set([
  'Price', 'BFreq', 'BLot', 'BLot/Freq', 'BOrd', 'BLot/Ord',
  'SLot', 'SFreq', 'SLot/Freq', 'SOrd', 'SLot/Ord',
  'TFreq', 'TLot', 'TOrd',
  // Haka Haki columns
  'HAKI', 'HAKA', 'Bor', 'Sor', 'Bfreq', 'Sfreq', 'HAKI/O', 'HAKI/F', 'HAKA/O', 'HAKA/F',
]);

export interface DoneSummaryBlobPaths {
  /** done_summary_bundle/{YYYYMMDD}/{stock}.zip — preferred source. */
  bundlePath: string;
  /** Entry name inside `bundlePath` for this broker/fd/board combination. */
  entryName: string;
  /** Legacy per-file CSV path, used as a fallback for dates predating bundling. */
  legacyPath: string;
}

/**
 * Build the done_summary bundle/entry/legacy paths under trading-data-v2, mirroring
 * the /done-summary/:stockCode/:date route in backend/src/routes/brokerbreakdown.ts:
 * - broker 'All' -> entry broker_breakdown/All[_${fd}_${board}].csv
 *   (legacy: done_summary_broker_breakdown/{YYYYMMDD}/{stock}/All[_${fd}_${board}].csv)
 * - specific broker -> entry haka_haki/${broker}[_${fd}_${board}].csv
 *   (legacy: done_summary_haka_haki/{YYYYMMDD}/{stock}/${broker}[_${fd}_${board}].csv)
 */
export function getDoneSummaryBlobPath(
  stockCode: string,
  dateStr: string,
  broker: string,
  fd: string,
  board: string,
): DoneSummaryBlobPaths {
  const dateFormatted = dateStr.replace(/-/g, '');
  const bundlePath = `done_summary_bundle/${dateFormatted}/${stockCode}.zip`;
  const suffix = (fd === 'all' && board === 'all') ? '' : `_${fd}_${board}`;

  if (broker.toLowerCase() === 'all') {
    return {
      bundlePath,
      entryName: `broker_breakdown/All${suffix}.csv`,
      legacyPath: `done_summary_broker_breakdown/${dateFormatted}/${stockCode}/All${suffix}.csv`,
    };
  }

  return {
    bundlePath,
    entryName: `haka_haki/${broker}${suffix}.csv`,
    legacyPath: `done_summary_haka_haki/${dateFormatted}/${stockCode}/${broker}${suffix}.csv`,
  };
}

/**
 * Parse done_summary CSV data, mirroring the parsing/filtering logic in the
 * /done-summary/:stockCode/:date route: numeric fields are converted to numbers,
 * and rows where every field is 0/empty are filtered out.
 */
export function parseDoneSummaryCSV(csvData: string): Record<string, unknown>[] {
  const lines = csvData.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = (lines[0] ?? '').split(',').map(h => h.trim());
  const rows: Record<string, unknown>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = (lines[i] ?? '').split(',').map(v => v.trim());
    const row: Record<string, unknown> = {};
    headers.forEach((header, idx) => {
      const value = values[idx] ?? '';
      row[header] = NUMERIC_FIELDS.has(header) ? (parseFloat(value) || 0) : value;
    });

    const hasData = headers.some(header => {
      const value = row[header];
      if (typeof value === 'number') return value !== 0 && !isNaN(value);
      return typeof value === 'string' && value.trim() !== '';
    });
    if (hasData) rows.push(row);
  }

  return rows;
}

const CSV_CACHE_NAME = 'done-summary-csv-cache';
const ZIP_FETCH_ATTEMPTS = 2;

/**
 * Fetch the legacy per-file done_summary CSV blob from Azure (via SAS URL), using
 * the browser Cache API for client-side caching, mirroring fetchBrokerSummaryCsv()
 * in brokerSummaryBlob.ts. Returns the CSV text, or null if the blob does not exist
 * (HTTP 404).
 */
async function fetchLegacyCsv(legacyPath: string, blobUrl: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const cache = await caches.open(CSV_CACHE_NAME);
    const cached = await cache.match(blobCacheKey(legacyPath));
    if (cached) {
      return cached.text();
    }

    const response = await fetch(blobUrl, signal ? { signal } : undefined);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errText.substring(0, 200)}`);
    }

    const text = await response.text();
    await cache.put(blobCacheKey(legacyPath), new Response(text, { headers: { 'Content-Type': 'text/csv' } }));
    return text;
  } catch (err: any) {
    if (err.name === 'AbortError') throw err;
    if (err.message?.startsWith('HTTP ')) throw err;
    // Cache API unavailable - fallback to direct fetch
    const response = await fetch(blobUrl, signal ? { signal } : undefined);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${legacyPath} not found`);
    return response.text();
  }
}

/**
 * Fetch a done_summary CSV, preferring the per-stock ZIP bundle
 * (done_summary_bundle/{date}/{stock}.zip) via range reads. If the bundle entry
 * can't be read after a couple of attempts, falls back to the legacy per-file CSV
 * (for dates that predate bundling). Returns null if neither source has the data.
 */
export async function fetchDoneSummaryCsv(
  paths: DoneSummaryBlobPaths,
  containerUrl: string,
  sasToken: string,
  signal?: AbortSignal,
): Promise<string | null> {
  const isDev = (import.meta as any).env?.DEV || false;
  const buildUrl = (path: string) => isDev
    ? `/azure-blob-proxy/trading-data-v2/${path}?${sasToken}`
    : `${containerUrl}/${path}?${sasToken}`;

  const zipUrl = buildUrl(paths.bundlePath);
  for (let attempt = 1; attempt <= ZIP_FETCH_ATTEMPTS; attempt++) {
    try {
      const data = await fetchZipEntry(zipUrl, paths.entryName, signal);
      if (data !== null) return new TextDecoder().decode(data);
    } catch (err: any) {
      if (err?.name === 'AbortError') throw err;
    }
  }

  return fetchLegacyCsv(paths.legacyPath, buildUrl(paths.legacyPath), signal);
}
